import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Filter, Loader2, Trash2, Upload, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { leadListsApi } from '@/api/leads';
import { CsvMapper, type MappedRow } from '@/components/csv-mapper';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Loader } from '@/components/ui/loader';
import { Modal } from '@/components/ui/modal';
import { StatusPill } from '@/components/ui/status-pill';
import { Topbar } from '@/components/ui/topbar';
import { ApiError } from '@/lib/api-client';
import { qk } from '@/lib/query-keys';

/**
 * Lead list detail page — /leads/:listId
 *
 * Shows every lead in this list, tenant-scoped (backend filters by
 * location_id from the session). Header actions: Upload CSV. Filter bar
 * lets the operator narrow by status + vertical + free-text search.
 *
 * Everything runs client-side on the fetched page of leads. When we hit
 * scale (thousands per list), we'll move filters to the URL + server.
 */
export function LeadListDetailPage(): JSX.Element {
  const { listId = '' } = useParams<{ listId: string }>();
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [vertical, setVertical] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const detail = useQuery({
    queryKey: qk.leadLists.detail(listId),
    queryFn: () => leadListsApi.detail(listId),
    enabled: Boolean(listId),
  });

  // Aggregate counts computed server-side so the status/vertical chips
  // reflect the whole list, not just the current page.
  const statsQuery = useQuery({
    queryKey: ['lead-list-stats', listId],
    queryFn: () => leadListsApi.stats(listId),
    enabled: Boolean(listId),
  });

  // Server-side pagination — 100 leads per page, numbered page navigation.
  // Status chip filter is applied server-side so the total page count reflects
  // the filtered subset.
  const leadsQuery = useQuery({
    queryKey: [...qk.leadLists.leads(listId), status, page],
    queryFn: () =>
      leadListsApi.leadsIn(listId, {
        page,
        limit: 100,
        ...(status ? { status } : {}),
      }),
    enabled: Boolean(listId),
    placeholderData: (prev) => prev,
  });

  const leads = leadsQuery.data?.items ?? [];
  const totalPages = leadsQuery.data?.total_pages ?? 1;
  const totalForFilter = leadsQuery.data?.total ?? 0;

  const statusCounts = statsQuery.data?.by_status ?? {};
  const verticalOptions = useMemo(() => {
    const vs = new Set<string>(Object.keys(statsQuery.data?.by_vertical ?? {}));
    for (const l of leads) if (l.main_vertical) vs.add(l.main_vertical);
    return Array.from(vs).sort();
  }, [statsQuery.data, leads]);

  // Client-side filters that DON'T need a full refetch — search + vertical
  // narrow the current page only. Status is server-side because it changes
  // total page count.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (vertical && l.main_vertical !== vertical) return false;
      if (q) {
        const hay = `${l.email} ${l.first_name ?? ''} ${l.last_name ?? ''} ${l.company ?? ''} ${l.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, vertical, search]);

  const emptyList = useMutation({
    mutationFn: () => leadListsApi.emptyList(listId),
    onSuccess: async () => {
      setPage(1);
      await qc.invalidateQueries({ queryKey: qk.leadLists.detail(listId) });
      await qc.invalidateQueries({ queryKey: qk.leadLists.leads(listId) });
      await qc.invalidateQueries({ queryKey: ['lead-list-stats', listId] });
      await qc.invalidateQueries({ queryKey: qk.leadLists.all });
    },
  });

  if (detail.isLoading) {
    return (
      <>
        <Topbar title="Loading…" />
        <div className="flex-1 flex items-center justify-center"><Loader /></div>
      </>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <>
        <Topbar title="Lead list" />
        <div className="flex-1 p-6">
          <ErrorState title="Couldn't load this list" onRetry={() => void detail.refetch()} />
        </div>
      </>
    );
  }

  const list = detail.data;

  return (
    <>
      <Topbar
        title={list.name}
        description={
          <span className="flex items-center gap-2">
            <Link to="/leads" className="text-mb-text-muted hover:text-mb-text">
              <ArrowLeft className="h-3 w-3 inline mr-0.5" />
              All lists
            </Link>
            <span className="text-mb-text-subtle">·</span>
            <span>{list.total_leads} leads</span>
            {list.main_vertical ? <><span className="text-mb-text-subtle">·</span><span>{list.main_vertical}</span></> : null}
          </span>
        }
        actions={
          <div className="flex gap-2">
            {list.total_leads > 0 ? (
              <button
                className="mb-btn-secondary text-mb-danger"
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete all ${list.total_leads} leads from "${list.name}"? This can't be undone.`,
                    )
                  ) {
                    emptyList.mutate();
                  }
                }}
                disabled={emptyList.isPending}
                title="Empty this list — keeps the list, deletes every lead in it"
              >
                {emptyList.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Delete all leads
              </button>
            ) : null}
            <button className="mb-btn-primary" onClick={() => setUploadOpen(true)}>
              <Upload className="h-4 w-4" strokeWidth={2} />
              Upload CSV
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {/* Status counts — from server-side stats, so accurate across the whole list */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {[
            { key: '', label: 'All', n: statsQuery.data?.total ?? list.total_leads },
            { key: 'pending', label: 'Pending', n: statusCounts.pending ?? 0 },
            { key: 'replied', label: 'Replied', n: statusCounts.replied ?? 0 },
            { key: 'interested', label: 'Interested', n: statusCounts.interested ?? 0 },
            { key: 'meeting_booked', label: 'Meeting', n: statusCounts.meeting_booked ?? 0 },
            { key: 'unsubscribed', label: 'Unsubscribed', n: statusCounts.unsubscribed ?? 0 },
          ].map((c) => (
            <button
              key={c.key || 'all'}
              type="button"
              onClick={() => {
                setStatus(c.key);
                setPage(1);
              }}
              className={
                'rounded-md border px-3 py-2 text-left transition-colors ' +
                (status === c.key
                  ? 'border-mb-accent bg-mb-accent-soft'
                  : 'border-mb-border bg-mb-surface hover:border-mb-text-muted')
              }
            >
              <div className="text-[10px] uppercase tracking-wider text-mb-text-subtle">{c.label}</div>
              <div className="text-lg font-semibold text-mb-text">{c.n}</div>
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-mb-border bg-mb-surface p-2">
          <Filter className="h-4 w-4 text-mb-text-muted ml-1" />
          <input
            type="search"
            placeholder="Search email / name / company"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-input min-w-64 flex-1"
          />
          <select value={vertical} onChange={(e) => setVertical(e.target.value)} className="mb-input">
            <option value="">All verticals</option>
            {verticalOptions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <div className="text-xs text-mb-text-muted ml-2">
            Page <b>{page}</b> of {totalPages} · showing <b>{filtered.length}</b> of {leads.length} on this page
            {status ? <> · {totalForFilter} total match "{status}"</> : statsQuery.data ? <> · {statsQuery.data.total} total in list</> : null}
          </div>
        </div>

        {/* Leads grid */}
        {leadsQuery.isLoading ? (
          <div className="flex justify-center py-10"><Loader /></div>
        ) : leadsQuery.isError ? (
          <ErrorState onRetry={() => void leadsQuery.refetch()} />
        ) : leads.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No leads yet"
            description="Click Upload CSV to import leads into this list."
          />
        ) : filtered.length === 0 ? (
          <div className="text-sm text-mb-text-muted text-center py-8">
            No leads match your filters. Clear them to see all {leads.length}.
          </div>
        ) : (
          <div className="mb-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-mb-surface">
                <tr className="text-left text-xs uppercase tracking-wider text-mb-text-muted">
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Company</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Vertical</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-mb-border hover:bg-mb-surface-hover">
                    <td className="px-4 py-2 font-mono text-xs">{l.email}</td>
                    <td className="px-4 py-2">
                      {[l.first_name, l.last_name].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-4 py-2">{l.company ?? '—'}</td>
                    <td className="px-4 py-2 text-mb-text-muted text-xs max-w-xs truncate" title={l.description ?? ''}>
                      {l.description ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-mb-text-muted">{l.main_vertical ?? '—'}</td>
                    <td className="px-4 py-2"><StatusPill status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 ? (
              <div className="border-t border-mb-border bg-mb-surface py-2 flex items-center justify-center gap-1">
                <Pager page={page} totalPages={totalPages} onChange={setPage} />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <UploadCsvModal
        listId={listId}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
    </>
  );
}

function UploadCsvModal({
  listId,
  open,
  onClose,
}: {
  listId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    imported: number;
    duplicates: number;
    skipped: number;
    total: number;
  } | null>(null);

  const upload = useMutation({
    mutationFn: (leads: MappedRow[]) => leadListsApi.import(listId, leads),
    onSuccess: async (r) => {
      setResult({
        imported: r.imported,
        duplicates: r.duplicates,
        skipped: r.skipped,
        total: r.total,
      });
      await qc.invalidateQueries({ queryKey: qk.leadLists.detail(listId) });
      await qc.invalidateQueries({ queryKey: qk.leadLists.leads(listId) });
      await qc.invalidateQueries({ queryKey: ['lead-list-stats', listId] });
      await qc.invalidateQueries({ queryKey: qk.leadLists.all });
    },
  });

  function reset() {
    setResult(null);
    setUploadError(null);
    setPending(false);
  }

  async function handleConfirm(rows: MappedRow[]) {
    setUploadError(null);
    setPending(true);
    try {
      await upload.mutateAsync(rows);
    } catch (err) {
      setUploadError(formatImportError(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Upload leads from CSV" size="lg">
      {result ? (
        <div className="space-y-4">
          <div className="rounded-md border border-mb-success/30 bg-mb-success-soft p-4 text-sm">
            <div className="font-semibold text-mb-success mb-1">Imported successfully</div>
            <div>
              Added <b>{result.imported}</b> new lead{result.imported === 1 ? '' : 's'}.
            </div>
            {result.duplicates > 0 ? (
              <div className="mt-0.5 text-mb-text-muted">
                <b>{result.duplicates}</b> duplicate{result.duplicates === 1 ? '' : 's'} skipped
                (already in this list).
              </div>
            ) : null}
            {result.skipped > 0 ? (
              <div className="mt-0.5 text-mb-text-muted">
                <b>{result.skipped}</b> row{result.skipped === 1 ? '' : 's'} skipped
                (invalid email or unparseable data).
              </div>
            ) : null}
            <div className="mt-1 text-mb-text-muted">List total: <b>{result.total}</b> leads.</div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="mb-btn-secondary" onClick={reset}>Upload another</button>
            <button type="button" className="mb-btn-primary" onClick={() => { reset(); onClose(); }}>Done</button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {pending ? (
            <div className="flex items-center gap-2 rounded-md border border-mb-border bg-mb-surface p-3 text-xs text-mb-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading…
            </div>
          ) : (
            <CsvMapper
              confirmLabel="Import"
              onCancel={() => { reset(); onClose(); }}
              onConfirm={handleConfirm}
            />
          )}
          {uploadError ? (
            <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger whitespace-pre-line">
              {uploadError}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/**
 * Turn a 422 back into something the operator can act on. The API surfaces
 * per-field validation errors as `details.fields` keyed by dotted paths
 * like `body.leads.5.company`. Extract the row index + field name so we
 * can point them at the exact problem.
 */
function formatImportError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Upload failed';
  const fields = (err.details?.fields ?? {}) as Record<string, string>;
  const entries = Object.entries(fields);
  if (entries.length === 0) return err.message;
  const lines = entries.slice(0, 5).map(([path, msg]) => {
    // Paths look like 'body.leads.7.company' — extract row + field.
    const m = /leads\.(\d+)\.(\w+)/.exec(path);
    if (m) return `Row ${Number(m[1]) + 1}, "${m[2]}" — ${msg}`;
    return `${path} — ${msg}`;
  });
  const rest = entries.length > 5 ? `\n(+ ${entries.length - 5} more)` : '';
  return `${err.message}\n${lines.join('\n')}${rest}`;
}

/**
 * Page-number pager. Shows: « ‹ 1 ... 4 5 [6] 7 8 ... 96 › »
 * Ellipsis when there are too many pages to render inline.
 */
function Pager({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}): JSX.Element {
  const nums = paginationRange(page, totalPages);
  const go = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };
  return (
    <>
      <PagerBtn label="«" disabled={page === 1} onClick={() => go(1)} title="First page" />
      <PagerBtn label="‹" disabled={page === 1} onClick={() => go(page - 1)} title="Previous" />
      {nums.map((n, i) =>
        n === '...' ? (
          <span key={`e${i}`} className="px-2 text-mb-text-subtle">
            …
          </span>
        ) : (
          <button
            key={n}
            type="button"
            onClick={() => go(n)}
            className={
              'min-w-8 rounded-md px-2 py-1 text-xs transition-colors ' +
              (n === page
                ? 'bg-mb-accent text-mb-accent-fg font-semibold'
                : 'text-mb-text hover:bg-mb-bg-soft')
            }
          >
            {n}
          </button>
        ),
      )}
      <PagerBtn label="›" disabled={page === totalPages} onClick={() => go(page + 1)} title="Next" />
      <PagerBtn
        label="»"
        disabled={page === totalPages}
        onClick={() => go(totalPages)}
        title="Last page"
      />
    </>
  );
}

function PagerBtn({
  label,
  disabled,
  onClick,
  title,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  title: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="min-w-8 rounded-md px-2 py-1 text-xs text-mb-text hover:bg-mb-bg-soft disabled:text-mb-text-subtle disabled:hover:bg-transparent"
    >
      {label}
    </button>
  );
}

/**
 * Build the list of page numbers (with ellipses) to show around the
 * current page. Always shows first + last; ellipses fill gaps > 1.
 * Example (page=6, total=96): [1, '...', 4, 5, 6, 7, 8, '...', 96]
 */
function paginationRange(current: number, total: number): Array<number | '...'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const nearby = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  const sorted = Array.from(nearby)
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out: Array<number | '...'> = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push('...');
  }
  return out;
}
